import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import {
  TicketIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  BuildingOffice2Icon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'badge-danger' },
  in_progress: { label: 'In Progress', color: 'badge-warning' },
  awaiting_user: { label: 'Awaiting User', color: 'badge-info' },
  resolved: { label: 'Resolved', color: 'badge-success' },
  closed: { label: 'Closed', color: 'badge-neutral' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-surface-400' },
  medium: { label: 'Medium', color: 'text-blue-400' },
  high: { label: 'High', color: 'text-amber-400' },
  urgent: { label: 'Urgent', color: 'text-red-400 font-semibold' }
};

const CATEGORY_LABELS = {
  general: 'General',
  technical: 'Technical',
  billing: 'Billing',
  feature_request: 'Feature Request'
};

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: ''
  });
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    loadTickets();
    loadStats();
  }, [pagination.page, filters]);
  
  const loadTickets = async () => {
    try {
      setLoading(true);
      const params = { 
        page: pagination.page, 
        limit: 20,
        ...filters
      };
      // Remove empty filters
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      
      const response = await adminApi.getTickets(params);
      setTickets(response.data.tickets);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };
  
  const loadStats = async () => {
    try {
      const response = await adminApi.getTicketStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };
  
  const getStatusCount = (status) => {
    const stat = stats?.byStatus?.find(s => s._id === status);
    return stat?.count || 0;
  };
  
  const openCount = getStatusCount('open') + getStatusCount('in_progress') + getStatusCount('awaiting_user');
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Support Tickets</h1>
          <p className="text-surface-400 mt-1">
            {openCount > 0 ? `${openCount} open tickets requiring attention` : 'All tickets resolved'}
          </p>
        </div>
        <button onClick={loadTickets} className="btn-secondary">
          <ArrowPathIcon className="w-5 h-5" />
          Refresh
        </button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { status: 'open', label: 'Open', icon: ClockIcon, color: 'text-red-400' },
            { status: 'in_progress', label: 'In Progress', icon: ChatBubbleLeftIcon, color: 'text-amber-400' },
            { status: 'awaiting_user', label: 'Awaiting User', icon: UserIcon, color: 'text-blue-400' },
            { status: 'resolved', label: 'Resolved', icon: CheckCircleIcon, color: 'text-emerald-400' },
            { status: 'closed', label: 'Closed', icon: CheckCircleIcon, color: 'text-surface-400' }
          ].map(({ status, label, icon: Icon, color }) => (
            <button
              key={status}
              onClick={() => {
                setFilters(f => ({ ...f, status: f.status === status ? '' : status }));
                setPagination(p => ({ ...p, page: 1 }));
              }}
              className={`card hover:border-primary-500 transition-colors ${
                filters.status === status ? 'border-primary-500' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${color}`} />
                <div>
                  <p className="text-2xl font-bold text-surface-100">{getStatusCount(status)}</p>
                  <p className="text-xs text-surface-400">{label}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters(f => ({ ...f, status: e.target.value }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
            className="input"
          >
            <option value="">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          
          <select
            value={filters.category}
            onChange={(e) => {
              setFilters(f => ({ ...f, category: e.target.value }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
            className="input"
          >
            <option value="">All Categories</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          
          <select
            value={filters.priority}
            onChange={(e) => {
              setFilters(f => ({ ...f, priority: e.target.value }));
              setPagination(p => ({ ...p, page: 1 }));
            }}
            className="input"
          >
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_CONFIG).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          
          {(filters.status || filters.category || filters.priority) && (
            <button
              onClick={() => {
                setFilters({ status: '', category: '', priority: '' });
                setPagination(p => ({ ...p, page: 1 }));
              }}
              className="btn-ghost"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
      
      {/* Tickets Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>User</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Messages</th>
              <th>Last Activity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : tickets.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-8 text-surface-400">
                  No tickets found
                </td>
              </tr>
            ) : (
              tickets.map(ticket => (
                <tr 
                  key={ticket._id}
                  className={ticket.unreadByAdmin ? 'bg-primary-900/10' : ''}
                >
                  <td>
                    <div className="flex items-center gap-2">
                      {ticket.unreadByAdmin && (
                        <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" />
                      )}
                      <div>
                        <p className="font-medium text-surface-100 max-w-xs truncate">
                          {ticket.subject}
                        </p>
                        <p className="text-xs text-surface-500">
                          #{ticket._id.slice(-6).toUpperCase()}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-surface-700 flex items-center justify-center text-xs text-surface-300">
                        {ticket.userId?.firstName?.[0]}{ticket.userId?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="text-sm text-surface-200">
                          {ticket.userId?.firstName} {ticket.userId?.lastName}
                        </p>
                        <p className="text-xs text-surface-500">
                          {ticket.companyId?.name}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge-neutral">
                      {CATEGORY_LABELS[ticket.category]}
                    </span>
                  </td>
                  <td>
                    <span className={PRIORITY_CONFIG[ticket.priority]?.color}>
                      {PRIORITY_CONFIG[ticket.priority]?.label}
                    </span>
                  </td>
                  <td>
                    <span className={STATUS_CONFIG[ticket.status]?.color}>
                      {STATUS_CONFIG[ticket.status]?.label}
                    </span>
                  </td>
                  <td className="text-surface-300">
                    {ticket.messageCount}
                  </td>
                  <td className="text-surface-400 text-sm">
                    {formatDistanceToNow(new Date(ticket.lastMessageAt || ticket.updatedAt))} ago
                  </td>
                  <td>
                    <button
                      onClick={() => navigate(`/tickets/${ticket._id}`)}
                      className="btn-ghost btn-sm"
                    >
                      <EyeIcon className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-surface-400 text-sm">
            Page {pagination.page} of {pagination.pages} ({pagination.total} tickets)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
